import React, { useState, useCallback, useEffect } from "react"
import { Box, ThemeProvider, createTheme } from "@mui/material"

import { colors } from "@/components/colors"
import TimeRangeSlider from "./TimeRangeSlider.tsx"
import TimeInput from "./TimeInput.tsx"
import { TimeUtils } from "./timeUtils.ts"
import { TimeRangeSliderContainerProps } from "./timeTypes.ts"

const theme = createTheme({
  palette: {
    primary: {
      main: colors.pink["800"],
    },
  },
})

export default function TimeRangeSliderContainer({
  initialStartTime = 0,
  initialEndTime = 100,
  minDistance = 1,
  maxTime = 100,
  boundaryMin = 0,
  boundaryMax = 100,
  step = 1,
  onTimeChange = () => {},
  onSliding = () => {},
  containerStyle,
  format = "HH:MM",
  overlayComponent,
  overlayRender,
  overlayHeight = 100,
}: TimeRangeSliderContainerProps) {
  const [timeRange, setTimeRange] = useState<[number, number]>([initialStartTime, initialEndTime])

  useEffect(() => {
    setTimeRange([initialStartTime, initialEndTime])
  }, [initialStartTime, initialEndTime, maxTime])

  const handleTimeRangeChange = useCallback(
    (newRange: [number, number]) => {
      const clampedRange: [number, number] = [
        Math.max(boundaryMin, Math.min(newRange[0], boundaryMax)),
        Math.max(boundaryMin, Math.min(newRange[1], boundaryMax)),
      ]

      setTimeRange(clampedRange)
      onTimeChange({
        start: clampedRange[0],
        end: clampedRange[1],
        startFormatted: TimeUtils.format(clampedRange[0], format),
        endFormatted: TimeUtils.format(clampedRange[1], format),
        duration: clampedRange[1] - clampedRange[0],
      })
    },
    [onTimeChange, boundaryMin, boundaryMax, format],
  )

  const handleStartTimeInput = useCallback(
    (timeString: string) => {
      const newValue = TimeUtils.parse(timeString, format)
      if (newValue !== null) {
        const validStart = Math.max(
          boundaryMin,
          Math.min(newValue, Math.min(timeRange[1] - minDistance, boundaryMax)),
        )
        handleTimeRangeChange([validStart, timeRange[1]])
      }
    },
    [timeRange, minDistance, handleTimeRangeChange, boundaryMin, boundaryMax, format],
  )

  const handleEndTimeInput = useCallback(
    (timeString: string) => {
      const newValue = TimeUtils.parse(timeString, format)
      if (newValue !== null) {
        const validEnd = Math.min(
          boundaryMax,
          Math.max(newValue, Math.max(timeRange[0] + minDistance, boundaryMin)),
        )
        handleTimeRangeChange([timeRange[0], validEnd])
      }
    },
    [timeRange, minDistance, handleTimeRangeChange, boundaryMin, boundaryMax, format],
  )

  const handleSliderChange = useCallback(
    (event: Event, newValue: number | number[], activeThumb: number) => {
      if (!Array.isArray(newValue)) return

      const clampedValue: [number, number] = [
        Math.max(boundaryMin, Math.min(newValue[0], boundaryMax)),
        Math.max(boundaryMin, Math.min(newValue[1], boundaryMax)),
      ]

      const finalValue: [number, number] =
        activeThumb === -1
          ? clampedValue
          : activeThumb === 0
            ? [Math.min(clampedValue[0], clampedValue[1] - minDistance), clampedValue[1]]
            : [clampedValue[0], Math.max(clampedValue[1], clampedValue[0] + minDistance)]

      setTimeRange(finalValue)

      onSliding({
        start: finalValue[0],
        end: finalValue[1],
        startFormatted: TimeUtils.format(finalValue[0], format),
        endFormatted: TimeUtils.format(finalValue[1], format),
        duration: finalValue[1] - finalValue[0],
      })
    },
    [minDistance, boundaryMin, boundaryMax, onSliding, format],
  )

  const handleSliderChangeCommitted = useCallback(
    (event: Event, newValue: number | number[]) => {
      if (!Array.isArray(newValue)) {
        return
      }

      onTimeChange({
        start: timeRange[0],
        end: timeRange[1],
        startFormatted: TimeUtils.format(timeRange[0], format),
        endFormatted: TimeUtils.format(timeRange[1], format),
        duration: timeRange[1] - timeRange[0],
      })
    },
    [onTimeChange, timeRange, format],
  )

  return (
    <ThemeProvider theme={theme}>
      {/* PDF 출력용 래퍼(Printable*Section)가 슬라이더를 숨길 수 있도록 식별 클래스를 둔다.
          클래스가 없어 기존 숨김 규칙이 매칭되지 않았다. */}
      <Box
        className="TimeRangeSliderContainer"
        sx={{
          width: "100%",
          margin: "auto",
          ...containerStyle,
        }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            position: "relative",
          }}>
          <TimeInput
            value={TimeUtils.format(timeRange[0], format)}
            onChange={handleStartTimeInput}
            placeholder={
              format === "HH:MM:SS.ms" ? "00:00:00.0" : format === "HH:MM:SS" ? "00:00:00" : "00:00"
            }
            min={boundaryMin}
            max={timeRange[1] - minDistance}
            format={format}
          />

          <Box sx={{ flex: 1, position: "relative" }}>
            {(overlayRender || overlayComponent) && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: `${overlayHeight}px`,
                  pointerEvents: "none",
                  zIndex: 1,
                  px: 2,
                  pb: "4px",
                  display: "flex",
                  alignItems: "flex-end",
                }}>
                {overlayRender
                  ? overlayRender({
                      currentStart: timeRange[0],
                      currentEnd: timeRange[1],
                      maxTime,
                    })
                  : overlayComponent}
              </Box>
            )}
            <Box
              sx={{
                mt: overlayRender || overlayComponent ? `${overlayHeight - 8}px` : 0,
                pt: 0,
                position: "relative",
                zIndex: 2,
              }}>
              <TimeRangeSlider
                value={timeRange}
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderChangeCommitted}
                min={0}
                max={maxTime}
                step={step}
                minDistance={minDistance}
                boundaryMin={boundaryMin}
                boundaryMax={boundaryMax}
                format={format}
              />
            </Box>
          </Box>

          <TimeInput
            value={TimeUtils.format(timeRange[1], format)}
            onChange={handleEndTimeInput}
            placeholder={
              format === "HH:MM:SS.ms" ? "00:00:00.0" : format === "HH:MM:SS" ? "00:00:00" : "00:00"
            }
            min={timeRange[0] + minDistance}
            max={boundaryMax}
            format={format}
          />
        </Box>
      </Box>
    </ThemeProvider>
  )
}
